import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { getCalendarEventFeedbackWindowState } from 'shared-helpers/calendar';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  ApiDownloadButton,
  Badge,
  DisplayFormattedDate,
  EmptyIndicator,
  ModalButton,
  MoreOptions,
  Pagination,
  TextWithLineBreaks,
  User
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow,
  BoxTitleWithSelectedItems
} from 'components/common/box';
import { CalendarEventParticipantsSendFeedbackEmail } from 'components/calendar/feedback';
import {
  CalendarEventParticipantsAddedFilter,
  CalendarEventParticipantsAddedHeader,
  CalendarEventParticipantsAttributes,
  CalendarEventParticipantsEditCustomAttributes,
  CalendarEventParticipantsRemove,
  CalendarEventParticipantsToggleParticipation
} from '.';

import type { User as UserType } from 'shared-types';
import type {
  GetCalendarEventUsersRes,
  GetEmailTemplatesByTypeRes
} from 'shared-types/responses';
import type { CalendarFilters } from './CalendarEventParticipantsAddedFilter';
import type { CalendarEventParticipantsView } from './CalendarEventParticipantsViewSelector';

export type CalendarUser = UserType & {
  added: string;
  participated: boolean;
  participatedComment: string;
  status: 'added' | 'queued';
};

interface Props {
  getUpdateData?: (updateData: (data: any) => Promise<void>) => void;
}

const CalendarEventParticipantsAdded = ({ getUpdateData }: Props) => {
  const { dateNow: now } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { calendarEvent },
    serverConfig: { featureFlags },
    theme
  } = useReduxState();

  const router = useRouter();
  const queryKeys = Object.keys(router.query);

  const search = (router.query.search as CalendarFilters['search']) || '';
  const sortBy = (router.query.sortBy as CalendarFilters['sortBy']) || 'name';
  const view =
    (router.query.view as CalendarEventParticipantsView) || 'default';

  const { data, loading, updateData } = useApiData<GetCalendarEventUsersRes>({
    url:
      `/admin/calendar-events/${calendarEvent.id}/users` +
      (queryKeys.length > 0
        ? `?${queryKeys.map((key) => `${key}=${router.query[key]}`).join('&')}`
        : ''),
    onlyIf: Boolean(calendarEvent?.id)
  });

  const showFeedbackColumn =
    featureFlags.enableFeedbackForCalendarEvents &&
    calendarEvent.feedbackEnabled &&
    calendarEvent.hasFeedbackForm;

  // fetched here rather than in the send button, so that the button in the
  // header and the one on every participant row share a single request
  const { data: emailTemplatesData } = useApiData<GetEmailTemplatesByTypeRes>({
    url:
      `/admin/email-templates-data?type=calendar-event-feedback-request` +
      `&calendarEventId=${calendarEvent.id}`,
    onlyIf: showFeedbackColumn
  });

  const feedbackEmailTemplates = emailTemplatesData?.emailTemplates || [];

  const feedbackWindowState = getCalendarEventFeedbackWindowState({
    event: calendarEvent,
    now
  });

  const updateUsers = async () => {
    await updateData();

    // update event data
    const eventsRequest = await api.get(
      `/admin/calendar-events?id=${calendarEvent.id}`
    );
    dispatch.updatePageState({
      calendarEvent: {
        ...calendarEvent,
        ...eventsRequest.data.events[0]
      }
    });
  };

  useEffect(() => {
    if (getUpdateData) getUpdateData(updateUsers);
  }, []);

  const handleFilters = ({
    search,
    sortBy,
    view: selectedView
  }: CalendarFilters) => {
    if (search !== undefined) {
      router.replace({ query: { ...router.query, page: 1, search } });
    }
    if (sortBy) {
      router.replace({ query: { ...router.query, sortBy } });
    }
    if (selectedView) {
      router.replace({ query: { ...router.query, view: selectedView } });
    }
  };

  return (
    <div>
      <Box
        headerContent={
          <CalendarEventParticipantsAddedHeader
            existingUserIds={data?.allUserIds || []}
            feedbackEmailTemplates={feedbackEmailTemplates}
            matchingUserIds={data?.allMatchingUserIds || []}
            updateUsers={updateUsers}
          />
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        subHeaderContent={
          <CalendarEventParticipantsAddedFilter
            onFilter={handleFilters}
            search={search}
            sortBy={sortBy}
            view={view}
          />
        }
        title={
          <BoxTitleWithSelectedItems
            numberOfItems={data?.allMatchingUserIds?.length || 0}
            title={messages.summaryAddedUsers}
          />
        }
      >
        {data?.users?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader
                withCheckbox={true}
                withCheckboxIdentifier="participants-added"
              >
                <BoxTableCell>{messages.commonUser}</BoxTableCell>

                <BoxTableCell>{messages.commonAdded}</BoxTableCell>

                <BoxTableCell>
                  {messages.eventCalendarParticipantParticipated}
                </BoxTableCell>

                <BoxTableCell shrink={true} />

                {showFeedbackColumn ? (
                  <BoxTableCell>{messages.commonFeedback}</BoxTableCell>
                ) : null}

                {calendarEvent.userAttributes?.length ? (
                  view === 'attributes' ? (
                    calendarEvent.userAttributes.map((attr) => (
                      <BoxTableCell key={attr.id}>
                        {attr.titleTranslations?.[language] || attr.title}
                      </BoxTableCell>
                    ))
                  ) : (
                    <BoxTableCell>
                      {messages.eventCalendarParticipantData}
                    </BoxTableCell>
                  )
                ) : null}

                <BoxTableCell shrink={true} />
              </BoxTableHeader>

              {data.users.map((user) => {
                const participatedData = user?.participated
                  ? {
                      color: theme.successColor,
                      icon: 'check' as const,
                      text: messages.commonYes
                    }
                  : {
                      color: theme.dangerColor,
                      icon: 'ban' as const,
                      text: messages.commonNo
                    };

                const feedbackBadgeData = user.feedbackSubmittedAt
                  ? {
                      category: 'success-light' as const,
                      text: messages.commonSubmitted,
                      tooltip: (
                        <div style={{ color: theme.mainTextColor }}>
                          <DisplayFormattedDate
                            date={user.feedbackSubmittedAt}
                            withTime={true}
                            withTimeStyles={{ color: 'inherit' }}
                          />
                        </div>
                      )
                    }
                  : user.roles?.includes('guest')
                  ? {
                      category: 'default' as const,
                      text: messages.eventCalendarFeedbackStatusNotAvailable,
                      tooltip:
                        messages.eventCalendarFeedbackStatusNotAvailableGuestTooltip
                    }
                  : !calendarEvent.feedbackAllowNonParticipated &&
                    !user.participated
                  ? {
                      category: 'default' as const,
                      text: messages.eventCalendarFeedbackStatusNotAvailable,
                      tooltip:
                        messages.eventCalendarFeedbackStatusNotAvailableTooltip
                    }
                  : feedbackWindowState === 'closed'
                  ? {
                      category: 'warning-light' as const,
                      text: messages.eventCalendarFeedbackStatusNotSubmitted,
                      tooltip: messages.eventCalendarFeedbackClosed
                    }
                  : {
                      category: 'warning-light' as const,
                      text: messages.eventCalendarFeedbackStatusPending,
                      tooltip: user.feedbackEmailSentAt
                        ? getMessageWithPlaceholders({
                            message: messages.eventCalendarFeedbackEmailSentOn,
                            placeholders: {
                              date: getFormattedDate({
                                date: user.feedbackEmailSentAt
                              })
                            }
                          })
                        : messages.eventCalendarFeedbackEmailNotSentYet
                    };

                return (
                  <BoxTableRow key={user.id} withCheckboxValue={user.id}>
                    <BoxTableCell>
                      <User enableProfileLink={true} user={user} />
                    </BoxTableCell>

                    <BoxTableCell>
                      <DisplayFormattedDate date={user.added} />
                    </BoxTableCell>

                    <BoxTableCell
                      icon={participatedData.icon}
                      iconStyle={{ color: participatedData.color }}
                    >
                      {participatedData.text}
                    </BoxTableCell>

                    <BoxTableCell>
                      {user.participatedComment ? (
                        <ModalButton
                          aria-label={messages.commonComment}
                          category="transparent"
                          data-tooltip={messages.commonComment}
                          icon="comment"
                          iconStyle={{ height: '80%', margin: 0, padding: 0 }}
                          modalProps={{
                            closeButton: true,
                            title: messages.commonComment
                          }}
                          size="medium"
                        >
                          <TextWithLineBreaks text={user.participatedComment} />
                        </ModalButton>
                      ) : null}
                    </BoxTableCell>

                    {showFeedbackColumn ? (
                      <BoxTableCell>
                        <Badge
                          category={feedbackBadgeData.category}
                          text={feedbackBadgeData.text}
                          tooltip={feedbackBadgeData.tooltip}
                        />
                      </BoxTableCell>
                    ) : null}

                    {calendarEvent.userAttributes?.length ? (
                      view === 'attributes' ? (
                        calendarEvent.userAttributes.map((attr) => {
                          const { type } = attr;
                          const value = user.userAttributes?.[attr.id];

                          return (
                            <BoxTableCell key={attr.id}>
                              {type === 'Boolean' && value !== undefined
                                ? value
                                  ? messages.commonYes
                                  : messages.commonNo
                                : Array.isArray(value)
                                ? value.join(', ')
                                : value}
                            </BoxTableCell>
                          );
                        })
                      ) : (
                        <BoxTableCell>
                          <CalendarEventParticipantsAttributes
                            user={user}
                            userAttributes={user.userAttributes}
                          />
                        </BoxTableCell>
                      )
                    ) : null}

                    <BoxTableCell>
                      <MoreOptions
                        renderAsFixed={true}
                        styleContentButtons={false}
                      >
                        <CalendarEventParticipantsEditCustomAttributes
                          updateUsers={updateUsers}
                          user={user}
                        />

                        <CalendarEventParticipantsToggleParticipation
                          action={user.participated ? 'remove' : 'grant'}
                          updateUsers={updateUsers}
                          user={user}
                        />

                        <CalendarEventParticipantsRemove
                          status="added"
                          updateUsers={updateUsers}
                          user={user}
                        />

                        {user.participated ? (
                          <ApiDownloadButton
                            filename={`${user.email || user.username}-cert.pdf`}
                            icon="file-pdf"
                            isDropdown={true}
                            text={messages.completionsDownloadCertificate}
                            url={
                              `/admin/calendar-event-certificate` +
                              `?eventId=${calendarEvent.id}` +
                              `&userId=${user.id}` +
                              `&language=${language}`
                            }
                          />
                        ) : null}

                        <CalendarEventParticipantsSendFeedbackEmail
                          emailTemplates={feedbackEmailTemplates}
                          updateUsers={updateUsers}
                          user={user}
                        />
                      </MoreOptions>
                    </BoxTableCell>
                  </BoxTableRow>
                );
              })}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="users" title={messages.commonNoUsers} />
        )}
      </Box>

      <Pagination
        currentPage={router.query.page ? Number(router.query.page) : 1}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => {
          router.replace({ query: { ...router.query, page } });
        }}
      />
    </div>
  );
};

export default CalendarEventParticipantsAdded;
